import { getDomElems } from '../../../../_shared-components/getDomElems';

/**
 * Manages aria-live regions to announce updates for screen readers.
 * Handles polite announcements, prevents overlapping messages, and debounces
 * updates to avoid excessive chatter.
 *
 * This is based on the example at https://alphagov.github.io/accessible-autocomplete/examples/
 * Which is from the UK's Government Digital Service
 *
 * Notably:
 * * it uses two swapping boxes (same as in the @rei/aria-announcer package) - this
 *   reduces the liklihood of announcements being skipped
 * * it uses a long delay - this is necessary due to the extremely long default
 *   screen reader chatter that occurs upon first focusing a combobox, and when
 *   typing in one. Without the delay, the only options are to interrupt that,
 *   and cause the user to use expected/critical information, or to chance losing
 *   the announcement (this was happening every time on first focus of the box
 *   for search history).
 *
 * This Handler requires three elements to initialize: The search input field
 * and the two feedback holders.
 * In main.js, it also is only set to initialize if either Search History or
 * Autosuggest is enabled
 * !Important: @private methods should not be used directly, eslint is unhappy
 * with # for private methods (not sure why, it should support, but out of scope
 * for current ticket)
 */
class AriaLiveHandler {
  constructor() {
    this.instance = null;
    this.enabled = false;
    this.debounceTimer = null;
    this.searchFieldEl = null;
    // Ensure 'this' is preserved for event callbacks
    this.abortPendingUpdate = this.abortPendingUpdate.bind(this);

    this.box1 = {
      hasText: false,
      el: null,
      setText(text) {
        this.el.textContent = text;
        this.hasText = true;
      },
      clearText() {
        this.el.textContent = '';
        this.hasText = false;
      },
    };

    this.box2 = {
      hasText: false,
      el: null,
      setText(text) {
        this.el.textContent = text;
        this.hasText = true;
      },
      clearText() {
        this.el.textContent = '';
        this.hasText = false;
      },
    };
  }

  static getInstance() {
    if (!AriaLiveHandler.instance) {
      AriaLiveHandler.instance = new AriaLiveHandler();
    }
    return AriaLiveHandler.instance;
  }

  /**
   * Initializes the aria-live handler by locating DOM elements and
   * attaching event listeners.
   *
   * @param {Object} selectors - keyed selectors for required elements (noted at top)
   * @param {string} selectors.ariaBox1 - Selector for the first aria-live element.
   * @param {string} selectors.ariaBox2 - Selector for the second aria-live element.
   * @param {string} selectors.searchField - Selector for the input field triggering updates.
   */
  init(selectors) {
    // returns null if selectors don't all return HTML el
    const ariaElems = getDomElems(selectors);

    // if any els missing, leave AriaLiveHandlers.enabled as false, exit
    if (!ariaElems) return;

    // otherwise, enable and track dom els in state
    this.enabled = true;
    this.box1.el = ariaElems.ariaBox1;
    this.box2.el = ariaElems.ariaBox2;
    this.searchFieldEl = ariaElems.searchField;

    // and add shared event listeners
    this.searchFieldEl.addEventListener('blur', this.abortPendingUpdate);
  }

  /**
   * Clears the text content of both aria-live boxes if they have text.
   * @private
   */
  clearAllBoxes() {
    if (this.box1.hasText) this.box1.clearText();
    if (this.box2.hasText) this.box2.clearText();
  }

  /**
   * Updates the appropriate aria-live region while ensuring debounce behavior.
   * Ensures that if a message queue exists, only the latest text is announced.
   *
   * @param {string} boxToSet - The key ('box1' or 'box2') of the box to update.
   * @param {string} otherBox - The key ('box1' or 'box2') of the box to clear.
   * @param {string} text - The text to announce.
   * @private
   */
  setTextByBox(boxToSet, otherBox, text) {
    clearTimeout(this.debounceTimer);

    if (!text) {
      this.clearAllBoxes();
      return;
    }

    this[otherBox].clearText();

    this.debounceTimer = setTimeout(() => {
      this[boxToSet].setText(text);
    }, 1000);
  }

  /**
   * Aborts any pending updates and clears the aria-live regions.
   * Prevents stale messages from being announced.
   */
  abortPendingUpdate() {
    if (!this.enabled) return;
    // This essentially clears the "aria-live" queue
    this.clearAllBoxes();

    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
  }

  /**
   * Updates the aria-live region with new text, alternating between two boxes.
   * Ensures that screen readers announce updates reliably without repeating old messages.
   *
   * @param {string} text - The text to announce via aria-live.
   */
  updateAriaLiveRegion(text) {
    if (!this.enabled) return;
    if (typeof text !== 'string') return;

    if (!this.box1.hasText) {
      this.setTextByBox('box1', 'box2', text);
    } else {
      this.setTextByBox('box2', 'box1', text);
    }
  }
}

export default AriaLiveHandler;
